import {Component, ErrorInfo, ReactNode} from "react";
import {observabilityError} from "tools/observability";
import {looksLikeStaleChunkError, reloadOnce} from "tools/stale-chunk";

type Props = {
  /** Optional fallback UI rendered when an error is caught. */
  fallback?: ReactNode;
  children?: ReactNode;
};

type State = {hasError: boolean};

/**
 * Catches render-time errors anywhere in the React tree and reports them
 * to Datadog + OpenReplay with the full component stack. Without this
 * boundary,
 * a thrown error inside a deeply-nested component results in a silent
 * white-screen — the error never reaches `window.onerror`, so the session
 * replay never sees it.
 *
 * Wraps the whole `<App />` so a single component crashing doesn't take
 * the entire UI down without telemetry. Children below get the chance to
 * provide their own narrower boundaries for per-page recovery.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = {hasError: false};

  static getDerivedStateFromError(): State {
    return {hasError: true};
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // React.lazy chunk failures land here: the lazy promise rejects, React
    // re-throws on the next render, and the global `unhandledrejection`
    // listener never sees it. Trigger a reload from this branch to recover
    // the same way the global listeners do for non-React failures.
    if (looksLikeStaleChunkError(error)) {
      reloadOnce();
      return;
    }

    observabilityError(error, {
      source: "react-error-boundary",
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
